/* @jsx h */
import { defineComponent, h, ref, onMounted, computed } from "vue";
import { useRoute, RouterLink } from "vue-router";
import { PublicHeaderComponent } from "../../components/PublicHeaderComponent";
import { PublicFooterComponent } from "../../components/PublicFooterComponent";
import { SeoHead } from "../../components/SeoHead";
import { useBranding } from "../../providers/Branding";
import {
  fetchShop,
  fetchGalleryAssets,
  fetchShopEvents,
  type GalleryAsset,
} from "../../../api/client";
import { EventWithShop, ShopProfile, ShopSetting } from "@lemmonhq/types";
import { EventsSection } from "./components/EventsSection";
import { RestaurantActions } from "./components/RestaurantActions";
import { RestaurantDetails } from "./components/RestaurantDetails";
import { RestaurantHeader } from "./components/RestaurantHeader";
import { RestaurantSidebar } from "./components/RestaurantSidebar";
import { useLocalization } from "@lemmonhq/uikit";

// Default placeholder image for restaurants without images
const PLACEHOLDER_IMAGE =
  "https://placehold.co/800x450/f3f4f6/9ca3af?text=Restaurant";

// Helper to get setting value from settings array
function getSettingValue(
  settings: ShopSetting[] | undefined,
  key: string,
): string | null {
  if (!settings) return null;
  const setting = settings.find((s) => s.key === key);
  return setting?.value ?? null;
}

function isSettingEnabled(
  settings: ShopSetting[] | undefined,
  key: string,
): boolean {
  const value = getSettingValue(settings, key);
  return value === "1" || value === "true";
}

export const RestaurantProfilePage = defineComponent({
  setup() {
    const route = useRoute();
    const { branding } = useBranding();
    const { t } = useLocalization();
    const restaurant = ref<ShopProfile | null>(null);
    const gallery = ref<GalleryAsset[]>([]);
    const events = ref<EventWithShop[]>([]);
    const eventsLoading = ref(true);
    const loading = ref(true);
    const error = ref<string | null>(null);
    const restaurantSlug = computed(() => route.params.id as string);

    const loadEvents = async (slug: string) => {
      try {
        eventsLoading.value = true;
        events.value = await fetchShopEvents(slug);
      } catch (eventsErr) {
        console.error("Failed to load events:", eventsErr);
        events.value = [];
      } finally {
        eventsLoading.value = false;
      }
    };

    const loadRestaurant = async () => {
      try {
        loading.value = true;
        error.value = null;

        // Fetch shop profile
        const shopData = await fetchShop(restaurantSlug.value);
        restaurant.value = shopData;
        await loadEvents(shopData.slug || restaurantSlug.value);

        // Fetch gallery assets
        try {
          const galleryData = await fetchGalleryAssets(restaurantSlug.value);
          gallery.value = galleryData;
        } catch (galleryErr) {
          console.warn("Failed to load gallery:", galleryErr);
          gallery.value = [];
        }
      } catch (err) {
        console.error("Failed to load restaurant data:", err);
        error.value = t("customer.restaurant_profile.failed_to_load");
      } finally {
        loading.value = false;
      }
    };

    onMounted(() => {
      loadRestaurant();
    });

    // Computed settings
    const reservationEnabled = computed(
      () => true, // isSettingEnabled(restaurant.value?.settings, "reservation_enabled")
    );
    const deliveryEnabled = computed(() =>
      isSettingEnabled(restaurant.value?.settings, "delivery"),
    );
    const pickupEnabled = computed(() =>
      isSettingEnabled(restaurant.value?.settings, "take_away"),
    );
    const onlineOrderingEnabled = computed(() =>
      isSettingEnabled(restaurant.value?.settings, "online_ordering_enabled"),
    );

    const reservationMinGuests = computed(() =>
      getSettingValue(restaurant.value?.settings, "reservation_min_guests"),
    );
    const reservationMaxGuests = computed(() =>
      getSettingValue(restaurant.value?.settings, "reservation_max_guests"),
    );
    const reservationAdvanceDays = computed(() =>
      getSettingValue(restaurant.value?.settings, "reservation_advance_days"),
    );

    return () => {
      if (loading.value) {
        return (
          <div class="min-h-screen flex items-center justify-center">
            <div class="text-center">
              <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
              <p class="text-gray-600">
                {t("customer.restaurant_profile.loading_restaurant")}
              </p>
            </div>
          </div>
        );
      }

      if (error.value || !restaurant.value) {
        return (
          <div class="min-h-screen flex items-center justify-center">
            <div class="text-center">
              <div class="text-6xl mb-6">🔍</div>
              <h1 class="text-2xl font-serif font-light text-gray-900 mb-4">
                {t("customer.restaurant_profile.not_found")}
              </h1>
              <p class="text-gray-600 font-light mb-8">
                {t("customer.restaurant_profile.not_found_description")}
              </p>
              <RouterLink
                to="/"
                class="inline-flex items-center px-6 py-3 border border-gray-900 text-gray-900 font-medium hover:bg-gray-900 hover:text-white transition-all duration-300"
              >
                {t("customer.home.back_to_directory")}
              </RouterLink>
            </div>
          </div>
        );
      }

      // SEO and structured data
      const restaurantName = restaurant.value.name || "";
      const pageTitle = `${restaurantName} | ${branding.value?.name} ${branding.value?.subtitle}`;
      const pageDescription =
        restaurant.value.description ||
        t("customer.restaurant_profile.default_meta_description", [
          restaurantName,
        ]);
      const pageKeywords = `${restaurantName}, restaurant, dining, food, menu, reservations, contact`;

      const structuredData = {
        "@context": "https://schema.org",
        "@type": "Restaurant",
        name: restaurantName,
        description: pageDescription,
        url: typeof window !== "undefined" ? window.location.href : "",
        image: gallery.value[0]?.url,
        address: {
          "@type": "PostalAddress",
          streetAddress: restaurant.value.address,
          addressLocality: restaurant.value.jurisdiction,
        },
        telephone: restaurant.value.phone,
        email: restaurant.value.email,
        geo: restaurant.value.geolocation
          ? {
              "@type": "GeoCoordinates",
              latitude: restaurant.value.geolocation.lat,
              longitude: restaurant.value.geolocation.lng,
            }
          : undefined,
      };

      // Use slug for routing to ShopHome style pages
      const shopSlug = restaurant.value.slug || restaurantSlug.value;
      const orderUrl = `/restaurant/${shopSlug}`;
      const reserveUrl = `/restaurant/${shopSlug}/reserve/tables`;
      const eventsUrl = `/restaurant/${shopSlug}/reserve/events`;

      return (
        <div class="min-h-screen">
          <SeoHead
            title={pageTitle}
            description={pageDescription}
            keywords={pageKeywords}
            image={gallery.value[0]?.url}
            type="restaurant"
            structuredData={structuredData}
          />
          <PublicHeaderComponent
            showBackButton={false}
            backTo="/"
            backText={t("customer.home.back_to_directory")}
          />

          <main class="max-w-6xl mx-auto px-4 py-12">
            <RestaurantHeader
              restaurant={restaurant.value}
              gallery={gallery.value}
              placeholderImage={PLACEHOLDER_IMAGE}
            />

            <RestaurantActions
              orderUrl={orderUrl}
              reserveUrl={reserveUrl}
              onlineOrderingEnabled={onlineOrderingEnabled.value}
              deliveryEnabled={deliveryEnabled.value}
              pickupEnabled={pickupEnabled.value}
              reservationEnabled={reservationEnabled.value}
            />

            {/* Content Grid */}
            <div class="grid grid-cols-1 lg:grid-cols-3 gap-12">
              {/* Main Content */}
              <div class="lg:col-span-2 space-y-12">
                <EventsSection
                  events={events.value}
                  loading={eventsLoading.value}
                  eventsUrl={eventsUrl}
                  shopSlug={shopSlug}
                />
                <RestaurantDetails
                  restaurant={restaurant.value}
                  gallery={gallery.value}
                  reservationEnabled={reservationEnabled.value}
                  deliveryEnabled={deliveryEnabled.value}
                  pickupEnabled={pickupEnabled.value}
                  onlineOrderingEnabled={onlineOrderingEnabled.value}
                  reservationMinGuests={reservationMinGuests.value}
                  reservationMaxGuests={reservationMaxGuests.value}
                  reservationAdvanceDays={reservationAdvanceDays.value}
                />
              </div>

              <RestaurantSidebar restaurant={restaurant.value} />
            </div>
          </main>

          <PublicFooterComponent />
        </div>
      );
    };
  },
});
